import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  UserPermission
} from '@globaleris/ui.library'
import { makeStyles } from '@material-ui/core'
import { Delete, Edit, Pageview } from '@material-ui/icons'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoCalculoConcepto } from '../../../../../api'
import { ConceptModifiedModel } from '../../../../../api/Argentia/endpoints/conceptsModified/model/conceptsModified'
import { EnumConceptType } from '../../../../../api/Argentia/enums/enumConceptType'
import { StandardCard } from '../../../../../components/shared/standardCard/standardCard'
import { enumToSelectOptionArray } from '../../../../../helpers'
import { getCustomerDomain } from '../../../../../redux'
import { ConceptModificationType } from '../../../../products/enums/conceptModificationType.enum'
import { buildDiscriminantTableColumns } from '../../../concepts.helpers'
import { mapConceptsModifiedAPItoTableModel } from '../../../concepts.mapper'
import { ConceptModifiedTableModel } from '../../../concepts.models'

const useStyles = makeStyles({
  cardNoMargin: {
    margin: '0 !important'
  }
})

interface ConceptModifiedCardProps {
  data: ConceptModifiedModel[] | undefined
  isLoading?: boolean
  onAddConceptModified?: () => void
  onEditConceptModified?: (selectedRow: ConceptModifiedTableModel) => void
  onDeleteConceptModified?: (selectedRow: ConceptModifiedTableModel) => void
  onViewPriceDetails?: (selectedRow: ConceptModifiedTableModel) => void
  conceptModificationType?: ConceptModificationType // Tipo de personalización: producto o contrato
}

export const ConceptModifiedCard: React.FC<ConceptModifiedCardProps> = ({
  data,
  isLoading,
  onAddConceptModified,
  onEditConceptModified,
  onDeleteConceptModified,
  onViewPriceDetails,
  conceptModificationType
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'conceptModifiedCard', 'conceptsEdit'])
  const classes = useStyles()

  const noData = data && data.length === 0

  // #region Handlers Starts

  const handleAddConceptModified = async (): Promise<boolean> => {
    if (onAddConceptModified) {
      onAddConceptModified()
    }
    return true
  }

  const handleEditConceptModified = (selectedRow: ConceptModifiedTableModel) => {
    if (onEditConceptModified) {
      onEditConceptModified(selectedRow)
    }
  }

  const handleDeleteConceptModified = (selectedRow: ConceptModifiedTableModel) => {
    if (onDeleteConceptModified) {
      onDeleteConceptModified(selectedRow)
    }
  }

  const handleViewPriceDetails = (selectedRow: ConceptModifiedTableModel) => {
    if (onViewPriceDetails) {
      onViewPriceDetails(selectedRow)
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts

  const getRowCustomActions = () => {
    const actions: any[] = []

    if (onViewPriceDetails) {
      actions.push({
        icon: <Pageview fontSize="small" />,
        tooltip: t('conceptModifiedCard:dataTable.tooltips.viewDetails'),
        onClick: handleViewPriceDetails,
        userPermission: UserPermission.EDIT,
        visibleIf: 'canViewPricingDetails'
      })
    }

    if (onEditConceptModified) {
      actions.push({
        icon: <Edit fontSize="small" />,
        tooltip: t('conceptModifiedCard:dataTable.tooltips.edit'),
        onClick: handleEditConceptModified,
        userPermission: UserPermission.EDIT
      })
    }

    if (onDeleteConceptModified) {
      actions.push({
        icon: <Delete fontSize="small" />,
        tooltip: t('conceptModifiedCard:dataTable.tooltips.delete'),
        onClick: handleDeleteConceptModified,
        userPermission: UserPermission.EDIT
      })
    }

    return actions
  }

  // #endregion Functions Ends

  // #region DataTable Config Starts

  const mappedData = useMemo(() => mapConceptsModifiedAPItoTableModel(data), [data])

  // Solo se incluyen columnas de discriminantes que tengan valor en al menos una fila,
  // evitando así pedir la configuración de layout por cada calculationType presente en la tabla.
  const discriminantColumns = useMemo(
    () => buildDiscriminantTableColumns(mappedData, t),
    [mappedData, t]
  )

  const conceptsModifiedDataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'masterConceptId',
      display: false
    },
    {
      name: 'code',
      label: t('conceptModifiedCard:dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('conceptModifiedCard:dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'customDescription',
      label: t('conceptModifiedCard:dataTable.columns.customDescription'),
      type: DataType.TEXT,
      display: conceptModificationType === ConceptModificationType.CONTRACT
    },
    {
      name: 'type',
      label: t('conceptModifiedCard:dataTable.columns.type'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumConceptType, 'enums:EnumTipoConcepto')
    },
    {
      name: 'calculationType',
      label: t('conceptModifiedCard:dataTable.columns.calculationType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumTipoCalculoConcepto,
        'enums:EnumTipoCalculoConcepto'
      )
    },
    {
      name: 'startDate',
      label: t('conceptModifiedCard:dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('conceptModifiedCard:dataTable.columns.endDate'),
      type: DataType.DATE
    },
    ...discriminantColumns
  ]

  const conceptsModifiedDataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: true,
    canEdit: false,
    canDelete: false,
    canInsert: !!onAddConceptModified,
    customInsertEditModal: true,
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('conceptModifiedCard:dataTable.analytics')
    },
    onRowInsert: onAddConceptModified ? handleAddConceptModified : undefined,
    rowCustomActions: getRowCustomActions()
  }

  // #endregion DataTable Config Ends

  return (
    <StandardCard
      className={classes.cardNoMargin}
      title={t('conceptModifiedCard:title')}
      titleVariant={'secondary'}
      isLoading={isLoading}
      showEmptyContent={noData}
      emptyCaption={t('conceptModifiedCard:emptyCaption')}
      emptyButtonCallback={onAddConceptModified}
      emptyButtonCaption={t('conceptModifiedCard:mainAction.add')}
    >
      {!noData && (
        <DataTable
          data={mappedData}
          columns={conceptsModifiedDataTableColumns}
          options={conceptsModifiedDataTableOptions}
        />
      )}
    </StandardCard>
  )
}
